/**
 * PracticeAreasHub — Morgan Legal Group (morganlegalny.com)
 *
 * Раскладка карточек взята с practice-areas соседнего домена, айдентика — своя:
 * тёмная шапка navy с водяным словом, cream-полотно, карточки `.service-card`
 * с золотой линией надстрочника, Playfair в заголовках, DM Sans в тексте.
 *
 * Почему это отдельная страница, а не ContentPageTemplate. Прежняя версия
 * собиралась обобщённым шаблоном из маркированных списков, и адреса разделов
 * печатались ТЕКСТОМ в скобках — «Estate Planning — wills, trusts (/estate-planning/)».
 * Читатель видел адрес, но перейти по нему не мог: ссылок на странице не было
 * вовсе. Здесь каждая карточка — настоящий <a>.
 *
 * Состав. Двенадцать разделов — ровно те, у которых на этом сайте ЕСТЬ страница
 * (проверено по сборке 29.08). У соседнего домена их четырнадцать: Immigration и
 * Business Law сюда НЕ перенесены, потому что таких страниц здесь нет и карточка
 * вела бы в 404. Прежний список насчитывал одиннадцать при заявленных в шапке
 * двенадцати — не хватало Civil Rights, у которого страница есть. Теперь число в
 * шапке и число карточек совпадают.
 */
import { useEffect } from 'react';
import { Phone, Calendar, ArrowRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';
import SchemaJsonLd from '@/components/SchemaJsonLd';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { marketingPages } from '@/data/marketingPages';
import { pageDates } from '@/data/pageDates';

const SITE = 'https://www.morganlegalny.com';

interface Area {
  /** Надстрочник карточки — обещание клиенту, а не название услуги. */
  eyebrow: string;
  title: string;
  description: string;
  href: string;
}

const AREAS: Area[] = [
  {
    eyebrow: 'Protect Your Legacy',
    title: 'Estate Planning',
    description:
      'Wills, revocable and irrevocable trusts, powers of attorney and healthcare directives, built around your family and drafted to New York law.',
    href: '/estate-planning/',
  },
  {
    eyebrow: 'Protecting Seniors & Families',
    title: 'Elder Law',
    description:
      "Medicaid planning and long-term care strategy under New York's five-year lookback, nursing home placement, and elder financial abuse.",
    href: '/elder-law/',
  },
  {
    eyebrow: "Protecting Your Family's Future",
    title: 'Family Law',
    description:
      'Custody and visitation, child and spousal support, and orders of protection across the five boroughs and Long Island.',
    href: '/family-law/',
  },
  {
    eyebrow: 'Fighting For Justice',
    title: 'Civil Rights',
    description:
      'Representation for victims of police misconduct, discrimination and wrongful arrest, in state and federal court.',
    href: '/civil-rights/',
  },
  {
    eyebrow: 'Protecting Your Investment',
    title: 'Real Estate',
    description:
      'Residential and commercial transactions, deed transfers, life estates, co-op and condo matters, and title disputes.',
    href: '/real-estate/',
  },
  {
    eyebrow: 'Maximum Compensation For Your Injuries',
    title: 'Personal Injury',
    description:
      'Serious injury claims under Insurance Law § 5102 — motor vehicle accidents, slip and fall, and medical malpractice.',
    href: '/personal-injury/',
  },
  {
    eyebrow: "Guiding Estates Through Surrogate's Court",
    title: 'Probate',
    description:
      "Estate administration and litigation in New York Surrogate's Court: will contests, accountings, executor duties and creditor claims.",
    href: '/probate/',
  },
  {
    eyebrow: 'Protecting Your Legacy With Precision',
    title: 'Wills & Trusts',
    description:
      'Drafting and funding of wills, revocable living trusts, irrevocable trusts and special needs trusts, plus trust litigation.',
    href: '/trusts/',
  },
  {
    eyebrow: "Shield Your Wealth Before It's At Risk",
    title: 'Asset Protection',
    description:
      'Medicaid asset protection trusts, LLC structuring and judgment-proofing for New York families and business owners.',
    href: '/asset-protection/',
  },
  {
    eyebrow: "Protecting Those Who Can't Protect Themselves",
    title: 'Guardianship',
    description:
      'Article 81 (MHL) proceedings for incapacitated adults and Article 17-A (SCPA) for individuals with developmental disabilities.',
    href: '/guardianship/',
  },
  {
    eyebrow: "Ensure Your Affairs Are Managed When You Can't",
    title: 'Power of Attorney',
    description:
      "New York's statutory short form with enhanced provisions, healthcare proxies and living wills, drafted under the 2021 reforms.",
    href: '/power-of-attorney/',
  },
  {
    eyebrow: 'Protecting Your Rights Through Divorce',
    title: 'Matrimonial',
    description:
      'Contested and uncontested divorce, equitable distribution, prenuptial agreements and high-net-worth matrimonial actions.',
    href: '/matrimonial/',
  },
];

function buildSchema() {
  const p = marketingPages['practice-areas'];
  const canonical = `${SITE}${p.url}`;
  const dates = pageDates(p.url);

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      url: canonical,
      name: p.metaTitle,
      description: p.metaDescription,
      isPartOf: { '@id': `${SITE}/#website` },
      datePublished: dates.published,
      dateModified: dates.modified,
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', '.lead', '[data-speakable]'],
      },
      // Перечень нужен затем же, зачем карточки: страница-хаб отвечает на вопрос
      // «что вы ведёте», и ответ должен быть машиночитаемым, а не только видимым.
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: AREAS.length,
        itemListElement: AREAS.map((a, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: a.title,
          url: `${SITE}${a.href}`,
        })),
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Practice Areas', item: canonical },
      ],
    },
  ];
}

function HeroBanner() {
  const p = marketingPages['practice-areas'];
  return (
    <section className="relative bg-navy pt-28 lg:pt-32 pb-20 lg:pb-28 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
        <span
          className="absolute bottom-0 -right-8 font-display text-[10rem] lg:text-[16rem] font-bold text-white leading-none whitespace-nowrap"
          style={{ opacity: 0.03 }}
        >
          {p.heroHeadline.toUpperCase()}
        </span>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav aria-label="Breadcrumb" className="mb-6 font-body text-xs text-white/50 tracking-wide">
          <a href="/" className="hover:text-gold transition-colors">Home</a>
          <span className="mx-2 text-gold/60">/</span>
          <span className="text-white/80">Practice Areas</span>
        </nav>

        <div className="max-w-3xl">
          <div className="section-label mb-5 text-gold">{p.heroEyebrow}</div>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] mb-7">
            {p.heroHeadline}{' '}
            <span className="text-gold italic font-display">{p.heroHeadlineAccent}</span>
          </h1>
          <p
            className="lead font-body text-white/80 text-lg lg:text-xl leading-relaxed mb-9 max-w-2xl"
            data-speakable
          >
            {p.heroLead}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="/appointment/" className="btn-gold">
              <Calendar className="w-4 h-4" />
              Schedule Free Consultation
            </a>
            <a
              href="tel:8885291315"
              className="inline-flex items-center gap-2 border border-gold/40 text-gold font-body font-semibold px-6 py-3 transition-all duration-200 hover:bg-gold hover:text-navy"
            >
              <Phone className="w-4 h-4" />
              888-529-1315
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function AreaCard({ area, index, visible }: { area: Area; index: number; visible: boolean }) {
  const num = String(index + 1).padStart(2, '0');
  return (
    <a
      href={area.href}
      className={`service-card group block overflow-hidden transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
      }`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      {/* Номер водяным знаком — тот же приём, что у слова в шапке: он держит
          ритм сетки, но не спорит с заголовком за внимание. */}
      <span
        aria-hidden="true"
        className="pointer-events-none select-none absolute -top-6 right-0 font-display text-[7rem] lg:text-[9rem] font-bold leading-none text-navy/[0.05] group-hover:text-gold/[0.10] transition-colors duration-300"
      >
        {num}
      </span>

      <div className="relative z-10">
        <div className="section-label mb-4">
          {num} · {area.eyebrow}
        </div>
        <h2 className="font-display text-navy text-2xl lg:text-3xl font-bold leading-tight mb-3">
          {area.title}
        </h2>
        <p className="font-body text-navy/65 text-sm lg:text-base leading-relaxed mb-6 max-w-xl">
          {area.description}
        </p>
        <span className="inline-flex items-center gap-2 font-body text-xs font-semibold uppercase tracking-[0.15em] text-navy/50 group-hover:text-gold group-hover:gap-3 transition-all duration-200">
          Learn More
          <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </a>
  );
}

function AreasGrid() {
  const { ref, visible } = useScrollReveal(0.05);
  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-20 lg:py-28 bg-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
          {AREAS.map((area, i) => (
            <AreaCard key={area.href} area={area} index={i} visible={visible} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  const cta = marketingPages['practice-areas'].cta;
  if (!cta) return null;
  return (
    <section className="bg-gold py-16 lg:py-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-display text-navy text-3xl lg:text-4xl font-bold leading-tight mb-4">
          {cta.headline}
        </h2>
        <p className="font-body text-navy/75 text-base lg:text-lg leading-relaxed mb-8">{cta.body}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="/appointment/"
            className="inline-flex items-center justify-center gap-2 bg-navy text-white font-body font-semibold px-7 py-3 transition-all duration-200 hover:bg-navy/90"
          >
            <Calendar className="w-4 h-4" />
            Schedule Free Consultation
          </a>
          <a
            href="tel:8885291315"
            className="inline-flex items-center justify-center gap-2 border border-navy/40 text-navy font-body font-semibold px-7 py-3 transition-all duration-200 hover:bg-navy hover:text-white"
          >
            <Phone className="w-4 h-4" />
            888-529-1315
          </a>
        </div>
      </div>
    </section>
  );
}

function setHeadTag(selector: string, attr: 'content' | 'href', value: string) {
  const el = document.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
  if (el) (el as unknown as Record<string, string>)[attr] = value;
}

function useHead() {
  const p = marketingPages['practice-areas'];
  useEffect(() => {
    const canonical = `${SITE}${p.url}`;
    if (typeof document !== 'undefined') {
      document.title = p.metaTitle;
      setHeadTag('meta[name="description"]', 'content', p.metaDescription);
      setHeadTag('link[rel="canonical"]', 'href', canonical);
      setHeadTag('meta[property="og:title"]', 'content', p.metaTitle);
      setHeadTag('meta[property="og:description"]', 'content', p.metaDescription);
      setHeadTag('meta[property="og:url"]', 'content', canonical);
      setHeadTag('meta[name="twitter:title"]', 'content', p.metaTitle);
      setHeadTag('meta[name="twitter:description"]', 'content', p.metaDescription);
    }
  }, [p]);
}

export default function PracticeAreasHub() {
  useHead();
  return (
    <div className="min-h-screen bg-cream">
      <SchemaJsonLd schema={buildSchema()} />
      <Navbar />
      <main id="content-page">
        <HeroBanner />
        <AreasGrid />
        <CtaBand />
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
